import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CONNECT_ROLES, hasConnectRole } from '../utils/roles';

// The three ACON-60 tabs, gated by connect engagement roles. Order here is the
// order shown in the dropdown.
const MORE_ITEMS = [
  { path: '/invoices', label: 'Invoices', role: CONNECT_ROLES.INVOICES },
  { path: '/rmas', label: 'RMAs', role: CONNECT_ROLES.RMA },
  { path: '/shipments', label: 'Shipments', role: CONNECT_ROLES.SHIPMENTS },
];

/**
 * "More ▾" header dropdown (ACON-60). Renders after the Orders tab and only when
 * the user has at least one of the invoices/rma/shipments connect roles. Each
 * menu item is individually gated by its role.
 *
 * Props mirror AppHeader's internals so the trigger matches the other nav items:
 *   navBtn(path) → style object, isDark, primary, lightText.
 */
export default function MoreDropdown({ navBtn, isDark, primary, lightText }) {
  const navigate = useNavigate();
  const location = useLocation();
  const connectRoles = useSelector((state) => state.auth.connectRoles) || [];
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const items = MORE_ITEMS.filter((it) => hasConnectRole(connectRoles, it.role));

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  const isActive = items.some((it) => location.pathname.startsWith(it.path));
  // Reuse navBtn styling, forcing the active look when on any More route so the
  // trigger highlights consistently with the other tabs.
  const triggerStyle = { ...navBtn(isActive ? location.pathname : '__none__'), gap: 4 };

  const menuBg = isDark ? '#ffffff' : '#ffffff';
  const menuText = isDark ? '#111827' : (lightText || '#111827');

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        style={triggerStyle}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        More
        <span style={{ display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
          <svg viewBox="0 0 24 24" fill="none" width={14} height={14}
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 6,
            minWidth: 160,
            backgroundColor: menuBg,
            border: '1px solid rgba(0,0,0,0.12)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
            padding: 4,
            zIndex: 100,
          }}
        >
          {items.map((it) => {
            const active = location.pathname.startsWith(it.path);
            return (
              <button
                key={it.path}
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); navigate(it.path); }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: active ? `${primary}1a` : 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  padding: '9px 12px',
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? primary : menuText,
                  cursor: 'pointer',
                }}
              >
                {it.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
